/**
 * GraphEditorModal Component
 * Modal for editing an individual graph's configuration
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  AnalyticsGraphDto,
  AnalyticsGraphUpdateDto,
  GraphFilterUI,
  GraphType,
  DataSourceEntity,
  AggregationType,
  FilterOperator,
  DateGranularity,
  FieldTransform,
  SecondaryDimensionType,
  GRAPH_TYPES,
  AGGREGATION_TYPES,
  FILTER_OPERATORS,
  DATE_GRANULARITIES,
  FIELD_TRANSFORMS,
  SECONDARY_DIMENSION_TYPES,
  ChallengeFormFieldSelection,
  AnalyticsGraphFilterDto,
} from "@/api/types/dtos/analytics/AnalyticsDto";
import { useAvailableFields, useFieldsCatalog } from "@/api/hooks/analytics/useAnalytics";
import { useOrganization } from "@/contexts/OrganizationContext";
import { parseFieldKey } from "../utils/fieldParser";
import { isMapDisabledForSource } from "../utils/mapGuard";
import { ChallengeFormFieldSelectorModal } from "./ChallengeFormFieldSelectorModal";

interface GraphEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (graphData: AnalyticsGraphUpdateDto) => Promise<void>;
  graph: AnalyticsGraphDto;
  isLoading?: boolean;
}

interface GraphFormData {
  name: string;
  description: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping
  secondaryDimensionType: SecondaryDimensionType;
  secondaryDimensionDataSource: DataSourceEntity;
  secondaryDimensionField: string;
  secondaryDimensionGranularity: DateGranularity;
  // Field transformation
  fieldTransform: FieldTransform;
  filters: GraphFilterUI[];
}

const DEFAULT_GRAPH_FORM: GraphFormData = {
  name: "",
  description: "",
  graphType: "BAR",
  dataSourceEntity: "FARM",
  dataSourceField: "name",
  aggregationType: "COUNT",
  // Secondary dimension defaults
  secondaryDimensionType: "NONE",
  secondaryDimensionDataSource: "FARM",
  secondaryDimensionField: "",
  secondaryDimensionGranularity: "MONTH",
  // Field transform defaults
  fieldTransform: "NONE",
  filters: [],
};

// Conversion functions for filter formats
const convertFiltersForBackend = (filters: GraphFilterUI[]): AnalyticsGraphFilterDto[] => {
  return filters.map((filter) => {
    const { filterField, operator, values } = filter;
    if (operator === "BETWEEN") {
      return {
        filterField,
        filterOperator: operator,
        filterValue: values[0] || "",
        filterValue2: values[1] || "",
      };
    } else if (operator === "IN") {
      return {
        filterField,
        filterOperator: operator,
        filterValue: JSON.stringify(values),
      };
    } else {
      // EQUALS, NOT_EQUALS, GREATER_THAN, LESS_THAN
      return {
        filterField,
        filterOperator: operator,
        filterValue: values[0] || "",
      };
    }
  });
};

const convertFiltersFromBackend = (backendFilters: AnalyticsGraphFilterDto[]): GraphFilterUI[] => {
  const filtersByField = new Map<string, GraphFilterUI>();

  for (const bf of backendFilters) {
    if (bf.filterOperator === "IN") {
      const values = JSON.parse(bf.filterValue || "[]");
      filtersByField.set(bf.filterField, {
        filterField: bf.filterField,
        operator: "IN",
        values,
      });
    } else if (bf.filterOperator === "BETWEEN") {
      filtersByField.set(bf.filterField, {
        filterField: bf.filterField,
        operator: "BETWEEN",
        values: [bf.filterValue || "", bf.filterValue2 || ""],
      });
    } else {
      // EQUALS, NOT_EQUALS, GREATER_THAN, LESS_THAN
      const existing = filtersByField.get(bf.filterField);
      if (existing && existing.operator === bf.filterOperator) {
        existing.values.push(bf.filterValue || "");
      } else {
        filtersByField.set(bf.filterField, {
          filterField: bf.filterField,
          operator: bf.filterOperator as FilterOperator,
          values: [bf.filterValue || ""],
        });
      }
    }
  }

  return Array.from(filtersByField.values());
};

// Filter Card Component
interface FilterCardProps {
  filter: GraphFilterUI;
  fieldLabel: string;
  fieldType?: string;
  enumValues?: string[];
  dataSourceEntity?: DataSourceEntity;
  isAdvancedMode: boolean;
  availableFields: Array<{
    key: string;
    label: string;
    type?: string;
    enumValues?: string[];
    dataSourceEntity?: DataSourceEntity;
  }>;
  onUpdateOperator: (operator: FilterOperator) => void;
  onAddValue: (value: string) => void;
  onRemoveValue: (value: string) => void;
  onRemove: () => void;
}

const FilterCard: React.FC<FilterCardProps> = ({
  filter,
  fieldLabel,
  fieldType,
  enumValues,
  dataSourceEntity,
  isAdvancedMode,
  availableFields,
  onUpdateOperator,
  onAddValue,
  onRemoveValue,
  onRemove,
}) => {
  const [showValueInput, setShowValueInput] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [tempValue2, setTempValue2] = useState("");

  const handleAddValue = () => {
    if (filter.operator === "BETWEEN") {
      if (tempValue && tempValue2) {
        onAddValue(tempValue);
        onAddValue(tempValue2);
        setTempValue("");
        setTempValue2("");
        setShowValueInput(false);
      }
    } else if (tempValue.trim()) {
      onAddValue(tempValue.trim());
      setTempValue("");
      setShowValueInput(false);
    }
  };

  const handleQuickAdd = (value: string) => {
    onAddValue(value);
  };

  const getOperatorLabel = (op: FilterOperator) => {
    return FILTER_OPERATORS.find((o) => o.value === op)?.label || op;
  };

  const getDataSourceLabel = (source: DataSourceEntity | undefined) => {
    if (!source) return "";
    switch (source) {
      case "FARM":
        return "Finca";
      case "FARM_ADMIN":
        return "Administrador";
      case "CHALLENGE":
        return "Inspección";
      case "CHALLENGE_FORM":
        return "Formulario";
      default:
        return source;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1,
        borderColor: "divider",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {fieldLabel}
            {dataSourceEntity && (
              <Chip
                label={getDataSourceLabel(dataSourceEntity)}
                size="small"
                variant="outlined"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  borderColor: "text.secondary",
                }}
              />
            )}
            {isAdvancedMode && (
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
                sx={{ ml: 1, fontWeight: 400 }}
              >
                | {getOperatorLabel(filter.operator)}
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {isAdvancedMode && (
            <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
              <InputLabel>Operador</InputLabel>
              <Select
                value={filter.operator}
                onChange={(e) => onUpdateOperator(e.target.value as FilterOperator)}
                label="Operador"
              >
                {FILTER_OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <IconButton size="small" onClick={onRemove} color="error">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Values display */}
      {filter.operator === "BETWEEN" ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {filter.values.length >= 2 ? (
            <Chip
              label={`${filter.values[0]} and ${filter.values[1]}`}
              size="small"
              onDelete={() => {
                onRemoveValue(filter.values[0]);
                onRemoveValue(filter.values[1]);
              }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              {filter.values.length === 1
                ? `Desde: ${filter.values[0]} (agregar valor)`
                : "Agregar valores de rango"}
            </Typography>
          )}
          {filter.values.length < 2 && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {!showValueInput ? (
                <Chip
                  label="+ Agregar rango"
                  size="small"
                  clickable
                  onClick={() => setShowValueInput(true)}
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <TextField
                    size="small"
                    placeholder="Desde"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    sx={{ minWidth: 80 }}
                  />
                  <Typography variant="body2">a</Typography>
                  <TextField
                    size="small"
                    placeholder="Hasta"
                    value={tempValue2}
                    onChange={(e) => setTempValue2(e.target.value)}
                    sx={{ minWidth: 80 }}
                  />
                  <Button size="small" onClick={handleAddValue}>
                    Agregar
                  </Button>
                  <IconButton size="small" onClick={() => setShowValueInput(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          {filter.values.map((value) => (
            <Chip key={value} label={value} size="small" onDelete={() => onRemoveValue(value)} />
          ))}
          {!showValueInput ? (
            <Chip
              label="+ Agregar valor"
              size="small"
              clickable
              onClick={() => setShowValueInput(true)}
              color="primary"
              variant="outlined"
            />
          ) : (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {enumValues && enumValues.length > 0 ? (
                <Select
                  size="small"
                  value={tempValue}
                  onChange={(e) => {
                    setTempValue(e.target.value);
                    if (e.target.value) {
                      handleQuickAdd(e.target.value);
                      setShowValueInput(false);
                    }
                  }}
                  sx={{ minWidth: 120 }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Seleccionar...</em>
                  </MenuItem>
                  {enumValues
                    .filter((v) => !filter.values.includes(v))
                    .map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                </Select>
              ) : (
                <>
                  <TextField
                    size="small"
                    placeholder="Ingresar valor"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddValue()}
                    sx={{ minWidth: 120 }}
                  />
                  <Button size="small" onClick={handleAddValue}>
                    Agregar
                  </Button>
                </>
              )}
              <IconButton size="small" onClick={() => setShowValueInput(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Show enum chips for quick add when available */}
      {enumValues && enumValues.length > 0 && filter.operator !== "BETWEEN" && !showValueInput && (
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {enumValues
            .filter((v) => !filter.values.includes(v))
            .slice(0, 8)
            .map((val) => (
              <Chip
                key={val}
                label={val}
                size="small"
                variant="outlined"
                clickable
                onClick={() => handleQuickAdd(val)}
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          {enumValues.filter((v) => !filter.values.includes(v)).length > 8 && (
            <Chip
              label={`+${enumValues.filter((v) => !filter.values.includes(v)).length - 8} más`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem" }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

// Add Filter Control Component
interface AddFilterControlProps {
  availableFields: Array<{
    key: string;
    label: string;
    type?: string;
    enumValues?: string[];
    dataSourceEntity?: DataSourceEntity;
    category?: string;
  }>;
  isAdvancedMode: boolean;
  onAddFilter: (fieldKey: string, operator: FilterOperator, values: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const AddFilterControl: React.FC<AddFilterControlProps> = ({
  availableFields,
  isAdvancedMode,
  onAddFilter,
  disabled = false,
  isLoading = false,
}) => {
  const [fieldKey, setFieldKey] = useState("");
  const [operator, setOperator] = useState<FilterOperator>("IN");
  const [values, setValues] = useState<string[]>([]);
  const [singleValue, setSingleValue] = useState("");
  const [betweenFrom, setBetweenFrom] = useState("");
  const [betweenTo, setBetweenTo] = useState("");

  const selectedField = availableFields.find((f) => f.key === fieldKey);
  const enumValues = selectedField?.enumValues;

  // Group fields by data source
  const fieldsBySource = availableFields.reduce(
    (acc, field) => {
      const source = field.dataSourceEntity || "OTHER";
      if (!acc[source]) {
        acc[source] = [];
      }
      acc[source].push(field);
      return acc;
    },
    {} as Record<string, typeof availableFields>
  );

  const handleAddFilter = () => {
    if (!fieldKey) return;

    let finalValues: string[] = [];
    let finalOperator = operator;

    if (isAdvancedMode) {
      if (operator === "IN") {
        finalValues = values;
      } else if (operator === "BETWEEN") {
        if (!betweenFrom || !betweenTo) return;
        finalValues = [betweenFrom, betweenTo];
      } else {
        if (!singleValue.trim()) return;
        finalValues = [singleValue.trim()];
      }
    } else {
      // Simple mode - always IN
      finalOperator = "IN";
      finalValues = values;
    }

    if (finalValues.length === 0) return;

    onAddFilter(fieldKey, finalOperator, finalValues);

    // Reset form
    setFieldKey("");
    setOperator("IN");
    setValues([]);
    setSingleValue("");
    setBetweenFrom("");
    setBetweenTo("");
  };

  const handleFieldChange = (newFieldKey: string) => {
    setFieldKey(newFieldKey);
    // Reset values when field changes
    setValues([]);
    setSingleValue("");
    setBetweenFrom("");
    setBetweenTo("");
  };

  const getDataSourceLabel = (source: DataSourceEntity) => {
    switch (source) {
      case "FARM":
        return "Finca";
      case "FARM_ADMIN":
        return "Administrador";
      case "CHALLENGE":
        return "Inspección";
      case "CHALLENGE_FORM":
        return "Campo de Formulario";
      default:
        return source;
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Campo de Filtro</InputLabel>
        <Select
          value={fieldKey}
          onChange={(e) => handleFieldChange(e.target.value)}
          label="Campo de Filtro"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <MenuItem disabled>
              <em>Cargando...</em>
            </MenuItem>
          ) : availableFields.length === 0 ? (
            <MenuItem disabled>
              <em>Sin campos disponibles</em>
            </MenuItem>
          ) : (
            Object.entries(fieldsBySource).flatMap(([source, fields]) => [
              // Header MenuItem
              ...(Object.keys(fieldsBySource).length > 1
                ? [
                    <MenuItem
                      key={`header-${source}`}
                      disabled
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        opacity: 1,
                      }}
                    >
                      {getDataSourceLabel(source as DataSourceEntity)}
                    </MenuItem>,
                  ]
                : []),
              // Field MenuItems
              ...fields.map((field) => (
                <MenuItem
                  key={field.key}
                  value={field.key}
                  sx={{ pl: field.dataSourceEntity ? 3 : 1 }}
                >
                  {field.label}
                </MenuItem>
              )),
            ])
          )}
        </Select>
      </FormControl>

      {isAdvancedMode && (
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Operador</InputLabel>
          <Select
            value={operator}
            onChange={(e) => setOperator(e.target.value as FilterOperator)}
            label="Operador"
            disabled={!fieldKey}
          >
            {FILTER_OPERATORS.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Value input based on mode and operator */}
      {!isAdvancedMode || operator === "IN" ? (
        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel>Valores</InputLabel>
          <Select
            multiple
            value={values}
            onChange={(e) => setValues(e.target.value as string[])}
            label="Valores"
            disabled={!fieldKey}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            )}
          >
            {enumValues && enumValues.length > 0 ? (
              enumValues.map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>Sin valores predefinidos</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      ) : operator === "BETWEEN" ? (
        <>
          <TextField
            size="small"
            label="Desde"
            value={betweenFrom}
            onChange={(e) => setBetweenFrom(e.target.value)}
            disabled={!fieldKey}
            sx={{ minWidth: 80 }}
          />
          <TextField
            size="small"
            label="Hasta"
            value={betweenTo}
            onChange={(e) => setBetweenTo(e.target.value)}
            disabled={!fieldKey}
            sx={{ minWidth: 80 }}
          />
        </>
      ) : (
        <TextField
          size="small"
          label="Valor"
          value={singleValue}
          onChange={(e) => setSingleValue(e.target.value)}
          disabled={!fieldKey}
          sx={{ minWidth: 120, flex: 1 }}
          placeholder={
            selectedField?.type === "number"
              ? "Ingresar número"
              : selectedField?.type === "date"
                ? "AAAA-MM-DD"
                : "Ingresar valor"
          }
        />
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={handleAddFilter}
        disabled={!fieldKey || (isAdvancedMode && operator === "IN" && values.length === 0)}
        startIcon={<AddIcon />}
      >
        Agregar Filtro
      </Button>
    </Box>
  );
};

// Dimension Field Selector Component - Reusable for primary and secondary dimensions
interface DimensionFieldSelectorProps {
  label: string;
  dataSource: DataSourceEntity;
  field: string;
  dimensionType?: SecondaryDimensionType;
  granularity?: DateGranularity;
  showDataSourceSelector?: boolean;
  onDataSourceChange: (source: DataSourceEntity) => void;
  onFieldChange: (field: string) => void;
  onGranularityChange?: (granularity: DateGranularity) => void;
  onOpenFormSelector: () => void;
  selectedFieldLabel?: string;
  getDisplayLabel: (fieldKey: string, dataSource?: DataSourceEntity) => string;
  organizationId: string;
  size?: "small" | "medium";
  /** Primary data source for reachability filtering. When provided, restricts the data source
   * dropdown to only show reachable sources for training primaries. */
  primaryDataSource?: DataSourceEntity;
}

const DimensionFieldSelector: React.FC<DimensionFieldSelectorProps> = ({
  label,
  dataSource,
  field,
  dimensionType = "NONE",
  granularity = "MONTH",
  showDataSourceSelector = false,
  onDataSourceChange,
  onFieldChange,
  onGranularityChange,
  onOpenFormSelector,
  selectedFieldLabel,
  getDisplayLabel,
  size = "small",
  primaryDataSource,
}) => {
  const { data: catalog = [] } = useFieldsCatalog();
  const catFields = catalog.find((c) => c.id === dataSource)?.fields ?? [];

  // Get available fields based on data source and dimension type (from catalog)
  const getAvailablePredefinedFields = () => {
    if (dimensionType === "TIME") {
      return catFields.filter((f) => f.kind === "DATE" || f.valueType === "date");
    } else if (dimensionType === "CATEGORICAL") {
      return catFields.filter((f) => f.valueType === "string" || f.valueType === "enum");
    }
    return catFields;
  };

  const availableFields = getAvailablePredefinedFields();
  const isFormField = dataSource === "CHALLENGE_FORM";

  return (
    <>
      {showDataSourceSelector && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size={size}>
            <InputLabel>Fuente de Datos</InputLabel>
            <Select
              value={dataSource}
              onChange={(e) => onDataSourceChange(e.target.value as DataSourceEntity)}
              label="Fuente de Datos"
            >
              {catalog
                .filter((c) =>
                  primaryDataSource
                    ? (catalog.find((p) => p.id === primaryDataSource)?.reachableSources ?? []).includes(c.id)
                    : true
                )
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {dimensionType !== "NONE" && (
        <>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: "block" }}>
              Selección Rápida
            </Typography>
            {isFormField ? (
              <Button
                variant="outlined"
                onClick={onOpenFormSelector}
                startIcon={<FolderOpenIcon />}
                fullWidth
                sx={{ height: size === "small" ? 40 : 56 }}
              >
                {selectedFieldLabel ||
                  (field
                    ? getDisplayLabel(field, dataSource)
                    : "Seleccionar Campo del Formulario...")}
              </Button>
            ) : (
              <FormControl fullWidth size={size}>
                <InputLabel>Campo</InputLabel>
                <Select
                  value={(() => {
                    // Strip data source prefix for comparison with predefined fields
                    const fieldKey = field?.includes(".") ? field.split(".", 2)[1] : field;
                    return availableFields.some((f) => f.key === fieldKey) ? fieldKey : "";
                  })()}
                  onChange={(e) => onFieldChange(e.target.value)}
                  label="Campo"
                >
                  <MenuItem value="">
                    <em>Seleccionar campo predefinido...</em>
                  </MenuItem>
                  {availableFields.map((f) => (
                    <MenuItem key={f.key} value={f.key}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          {!isFormField && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mb: 0.5, display: "block" }}
              >
                O Seleccionar desde Formularios
              </Typography>
              <Button
                variant="outlined"
                onClick={onOpenFormSelector}
                startIcon={<FolderOpenIcon />}
                fullWidth
                sx={{ height: 40 }}
              >
                {selectedFieldLabel || (field ? getDisplayLabel(field) : "Seleccionar Campo...")}
              </Button>
            </Grid>
          )}
        </>
      )}

      {dimensionType === "TIME" && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size={size}>
            <InputLabel>Granularidad</InputLabel>
            <Select
              value={granularity}
              onChange={(e) => onGranularityChange?.(e.target.value as DateGranularity)}
              label="Granularidad"
            >
              {DATE_GRANULARITIES.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
    </>
  );
};

export const GraphEditorModal: React.FC<GraphEditorModalProps> = ({
  open,
  onClose,
  onSave,
  graph,
  isLoading = false,
}) => {
  const { selectedOrganization } = useOrganization();
  const { data: catalog = [] } = useFieldsCatalog();
  const [graphForm, setGraphForm] = useState<GraphFormData>(DEFAULT_GRAPH_FORM);
  const [selectedFieldInfo, setSelectedFieldInfo] = useState<ChallengeFormFieldSelection | null>(
    null
  );
  const [fieldSelectorOpen, setFieldSelectorOpen] = useState(false);

  // Secondary dimension field selector
  const [dateFieldSelectorOpen, setDateFieldSelectorOpen] = useState(false);
  const [selectedDateFieldInfo, setSelectedDateFieldInfo] =
    useState<ChallengeFormFieldSelection | null>(null);

  // Map to store labels for form fields (key -> label) - persists across editing sessions
  const [fieldLabelsMap, setFieldLabelsMap] = useState<Map<string, string>>(new Map());

  // Track if form has been initialized to prevent resetting on dynamicFields changes
  const hasInitializedRef = useRef<string | null>(null);

  // Graph filter form state (new simplified)
  const [isAdvancedFilterMode, setIsAdvancedFilterMode] = useState(false);
  const [newFilterField, setNewFilterField] = useState("");
  const [newFilterOperator, setNewFilterOperator] = useState<FilterOperator>("IN");
  const [newFilterValues, setNewFilterValues] = useState<string[]>([]);
  const [newFilterValue, setNewFilterValue] = useState(""); // For single value operators
  const [newFilterValue2, setNewFilterValue2] = useState(""); // For BETWEEN operator

  // Dynamic fields for CHALLENGE_FORM data source
  const { data: dynamicFields = [], isLoading: isLoadingFields } = useAvailableFields(
    graphForm.dataSourceEntity
  );

  // Catalog-based helpers (replaces hardcoded PREDEFINED_FIELDS, DATA_SOURCE_LABELS, reachability)
  const getCatalogFields = (source: DataSourceEntity) =>
    catalog.find((c) => c.id === source)?.fields ?? [];
  const getCatalogLabel = (source: DataSourceEntity) =>
    catalog.find((c) => c.id === source)?.label ?? source;
  const isReachable = (primary: DataSourceEntity, target: DataSourceEntity): boolean => {
    const entry = catalog.find((c) => c.id === primary);
    if (!entry) return true;
    return entry.reachableSources.includes(target);
  };
  // While the catalog is loading, fall back to the static guard so MAP-capable sources
  // are not wrongly reported as unsupported (which flipped MAP graphs to BAR on open).
  const supportsMap = (source: DataSourceEntity): boolean =>
    catalog.find((c) => c.id === source)?.supportsMap ?? !isMapDisabledForSource(source);
  const getFieldLabel = (source: DataSourceEntity, fieldKey: string): string => {
    const cleanKey = fieldKey.includes(".") ? fieldKey.split(".", 2)[1] : fieldKey;
    return getCatalogFields(source).find((f) => f.key === cleanKey)?.label ?? fieldKey;
  };

  // Helper function to get display label for any field key
  const getDisplayLabelForKey = (fieldKey: string, dataSourceEntity?: DataSourceEntity): string => {
    // First check stored labels from form selection
    const storedLabel = fieldLabelsMap.get(fieldKey);
    if (storedLabel) {
      return storedLabel;
    }

    // Check predefined fields
    if (dataSourceEntity) {
      const predefinedField = getCatalogFields(dataSourceEntity ?? graphForm.dataSourceEntity).find((f) => f.key === fieldKey);
      if (predefinedField) {
        return predefinedField.label;
      }
    }

    // Check dynamic fields
    const dynamicField = dynamicFields.find((f) => f.key === fieldKey);
    if (dynamicField) {
      return dynamicField.label;
    }

    // Fallback to key itself (truncated if UUID)
    if (fieldKey.includes("-") && fieldKey.length > 30) {
      return `Campo (${fieldKey.substring(0, 8)}...)`;
    }
    return fieldKey;
  };

  // Initialize form with graph data when opened
  // Only initialize once per graph id to prevent resetting on dynamicFields changes
  useEffect(() => {
    if (graph && open && hasInitializedRef.current !== graph.id) {
      hasInitializedRef.current = graph.id;
      // Convert backend filters to UI format
      const uiFilters = convertFiltersFromBackend(graph.filters || []);

      // Parse secondary dimension field - may include data source prefix (e.g., "FARM.region")
      let secondaryDataSource: DataSourceEntity = graph.dataSourceEntity; // Default to primary
      let secondaryField = graph.secondaryDimensionField || "";

      if (secondaryField && secondaryField.includes(".")) {
        const [prefix, field] = secondaryField.split(".", 2);
        const catEntry = catalog.find((c) => c.id === prefix);
        if (catEntry) {
          secondaryDataSource = prefix as DataSourceEntity;
          secondaryField = field;
        }
      }

      setGraphForm({
        name: (graph.name || ''),
        description: graph.description,
        graphType: graph.graphType,
        dataSourceEntity: graph.dataSourceEntity,
        dataSourceField: graph.dataSourceField,
        aggregationType: graph.aggregationType,
        // Secondary dimension
        secondaryDimensionType: (graph.secondaryDimensionType as SecondaryDimensionType) || "NONE",
        secondaryDimensionDataSource: secondaryDataSource,
        secondaryDimensionField: secondaryField,
        secondaryDimensionGranularity: graph.secondaryDimensionGranularity || "MONTH",
        // Field transform
        fieldTransform: (graph.fieldTransform as FieldTransform) || "NONE",
        filters: uiFilters,
      });

      // Parse CHALLENGE_FORM field info and populate labels
      if (graph.dataSourceEntity === "CHALLENGE_FORM") {
        const parsedField = parseFieldKey(graph.dataSourceField) as any;
        // Try to get label from dynamic fields
        const dynamicField = dynamicFields.find((f) => f.key === graph.dataSourceField);
        const label = dynamicField?.label || graph.dataSourceField;

        if (parsedField || dynamicField) {
          const fieldInfo: ChallengeFormFieldSelection = {
            key: graph.dataSourceField,
            label: label,
            type: dynamicField?.type || "string",
            programName: "Program",
            versionName: "Version",
            itemName: "Item",
            fieldName: "Field",
            questionId: parsedField?.questionId || graph.dataSourceField,
            itemId: parsedField?.itemId,
            versionId: parsedField?.versionId,
            programId: parsedField?.programId,
            forEachContext: parsedField?.forEachValues,
          };
          setSelectedFieldInfo(fieldInfo);
          setFieldLabelsMap((prev) => new Map(prev).set(graph.dataSourceField, label));
        }
      } else {
        setSelectedFieldInfo(null);
      }

      // Populate secondary dimension field info
      if (graph.secondaryDimensionField) {
        const secondaryDynamicField = dynamicFields.find(
          (f) => f.key === graph.secondaryDimensionField
        );
        const secondaryLabel = secondaryDynamicField?.label || graph.secondaryDimensionField;

        if (secondaryDynamicField || graph.secondaryDimensionField) {
          setSelectedDateFieldInfo({
            key: graph.secondaryDimensionField,
            label: secondaryLabel,
            type: secondaryDynamicField?.type || "string",
            questionId: graph.secondaryDimensionField,
          });
          setFieldLabelsMap((prev) =>
            new Map(prev).set(graph.secondaryDimensionField || '', secondaryLabel)
          );
        }
      } else {
        setSelectedDateFieldInfo(null);
      }
    }
  }, [graph, open, dynamicFields]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = null;
    }
  }, [open]);

  // Auto-fall-back from MAP to BAR when the selected data source does not support MAP.
  // This covers loading an existing graph whose source was changed to a training source elsewhere.
  useEffect(() => {
    if (graphForm.graphType === "MAP" && !supportsMap(graphForm.dataSourceEntity)) {
      setGraphForm((prev) => ({ ...prev, graphType: "BAR" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphForm.graphType, graphForm.dataSourceEntity, catalog]);

  const resetFilterForm = () => {
    setNewFilterField("");
    setNewFilterOperator("IN");
    setNewFilterValues([]);
    setNewFilterValue("");
    setNewFilterValue2("");
  };

  const handleAddGraphFilter = (fieldKey: string, operator: FilterOperator, values: string[]) => {
    if (!fieldKey) return;

    setGraphForm((prev) => {
      const existingFilterIndex = prev.filters.findIndex((f) => f.filterField === fieldKey);

      if (existingFilterIndex >= 0) {
        // Update existing filter
        const updatedFilters = [...prev.filters];
        updatedFilters[existingFilterIndex] = {
          filterField: fieldKey,
          operator,
          values,
        };
        return { ...prev, filters: updatedFilters };
      } else {
        // Add new filter
        return {
          ...prev,
          filters: [...prev.filters, { filterField: fieldKey, operator, values }],
        };
      }
    });

    // Reset filter form
    setNewFilterField("");
    setNewFilterOperator("IN");
    setNewFilterValues([]);
    setNewFilterValue("");
    setNewFilterValue2("");
  };

  const handleRemoveGraphFilter = (filterField: string) => {
    setGraphForm({
      ...graphForm,
      filters: graphForm.filters.filter((f) => f.filterField !== filterField),
    });
  };

  const handleAddFilterValue = (filterField: string, value: string) => {
    if (!value.trim()) return;

    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField ? { ...f, values: [...f.values, value.trim()] } : f
      ),
    }));
  };

  const handleRemoveFilterValue = (filterField: string, valueToRemove: string) => {
    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField
          ? { ...f, values: f.values.filter((v) => v !== valueToRemove) }
          : f
      ),
    }));
  };

  const handleUpdateFilterOperator = (filterField: string, newOperator: FilterOperator) => {
    setGraphForm((prev) => ({
      ...prev,
      filters: prev.filters.map((f) =>
        f.filterField === filterField
          ? { ...f, operator: newOperator, values: newOperator === "BETWEEN" ? ["", ""] : f.values }
          : f
      ),
    }));
  };

  const getGraphFilterLabel = (filter: AnalyticsGraphFilterDto): string => {
    // First check stored labels from form selection
    const storedLabel = fieldLabelsMap.get(filter.filterField);
    const field = getAllFilterableFields().find((f) => f.key === filter.filterField);
    const fieldLabel = storedLabel || field?.label || filter.filterField;
    const operatorLabel =
      FILTER_OPERATORS.find((o) => o.value === filter.filterOperator)?.label ||
      filter.filterOperator;

    let valueLabel = "";
    if (filter.filterOperator === "IN") {
      const values = JSON.parse(filter.filterValue || "[]");
      valueLabel = values.join(", ");
    } else if (filter.filterOperator === "BETWEEN") {
      valueLabel = `${filter.filterValue} and ${filter.filterValue2}`;
    } else {
      valueLabel = filter.filterValue || "";
    }

    return `${fieldLabel} ${operatorLabel} ${valueLabel}`;
  };

  const getAvailableFields = () => {
    if (graphForm.dataSourceEntity === "CHALLENGE_FORM") {
      return dynamicFields.length > 0 ? dynamicFields : [];
    }
    return getCatalogFields(graphForm.dataSourceEntity);
  };

  // Get all filterable fields from all data sources (for graph filters)
  const getAllFilterableFields = () => {
    const allFields: Array<{
      key: string;
      label: string;
      type?: string;
      enumValues?: string[];
      category?: string;
      dataSourceEntity?: DataSourceEntity;
    }> = [];

    // Add fields from all catalog sources
    catalog.forEach((cat) => {
      cat.fields.forEach((field) => {
        allFields.push({
          key: field.key,
          label: field.label,
          type: field.valueType,
          enumValues: field.enumValues,
          category: field.category,
          dataSourceEntity: cat.id,
        });
      });
    });

    return allFields;
  };

  // Get field by key for filter cards
  const getFieldByKey = (fieldKey: string) => {
    return getAllFilterableFields().find((f) => f.key === fieldKey);
  };

  const handleSave = async () => {
    // Convert UI filters to backend format
    const backendFilters = convertFiltersForBackend(graphForm.filters);

    const updateData: AnalyticsGraphUpdateDto = {
      name: graphForm.name,
      description: graphForm.description,
      graphType: graphForm.graphType,
      dataSourceEntity: graphForm.dataSourceEntity,
      dataSourceField: graphForm.dataSourceField,
      aggregationType: graphForm.aggregationType,
      // MAP: secondaryDimensionField is used for transparency (no dimension type)
      secondaryDimensionType:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.secondaryDimensionType !== "NONE"
            ? graphForm.secondaryDimensionType
            : undefined,
      secondaryDimensionField:
        graphForm.graphType === "MAP"
          ? graphForm.secondaryDimensionField
          : graphForm.secondaryDimensionType !== "NONE"
            ? graphForm.secondaryDimensionField
            : undefined,
      secondaryDimensionGranularity:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.secondaryDimensionType === "TIME"
            ? graphForm.secondaryDimensionGranularity
            : undefined,
      // Field transform - not used for MAP
      fieldTransform:
        graphForm.graphType === "MAP"
          ? undefined
          : graphForm.fieldTransform !== "NONE"
            ? graphForm.fieldTransform
            : undefined,
      filters: backendFilters,
    };
    await onSave(updateData);
  };

  const handleChallengeFormFieldSelected = (field: ChallengeFormFieldSelection) => {
    setSelectedFieldInfo(field);
    setGraphForm({
      ...graphForm,
      dataSourceField: field.key,
    });
    // Store the label in the map for later display
    setFieldLabelsMap((prev) => new Map(prev).set(field.key, field.label));
    setFieldSelectorOpen(false);
  };

  const handleDateFieldSelected = (field: ChallengeFormFieldSelection) => {
    setSelectedDateFieldInfo(field);
    setGraphForm({
      ...graphForm,
      secondaryDimensionField: field.key,
    });
    // Store the label in the map for later display
    setFieldLabelsMap((prev) => new Map(prev).set(field.key, field.label));
    setDateFieldSelectorOpen(false);
  };

  if (!graph) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: "var(--tertiary)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "24px 32px",
            background: "linear-gradient(180deg, var(--tertiary) 0%, var(--background-paper) 100%)",
            borderBottom: "1px solid var(--border-color)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>
            Editar Gráfico
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={isLoading}
            sx={{
              color: "var(--text-secondary)",
              "&:hover": {
                color: "var(--primary)",
                backgroundColor: "var(--tertiary)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            padding: "24px 32px",
            backgroundColor: "var(--tertiary)",
            borderColor: "var(--border-color)",
          }}
        >
          <Grid container spacing={3}>
            {/* Graph Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Gráfico"
                value={graphForm.name}
                onChange={(e) => setGraphForm({ ...graphForm, name: e.target.value })}
                placeholder="Cantidad de Fincas por Región"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--input-border-radius)",
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descripción"
                value={graphForm.description}
                onChange={(e) => setGraphForm({ ...graphForm, description: e.target.value })}
                placeholder="Describe qué muestra este gráfico..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--input-border-radius)",
                  },
                }}
              />
            </Grid>

            {/* Graph Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Gráfico</InputLabel>
                <Select
                  value={graphForm.graphType}
                  onChange={(e) => {
                    const newType = e.target.value as GraphType;
                    if (newType === "MAP") {
                      setGraphForm({
                        ...graphForm,
                        graphType: newType,
                        dataSourceEntity: "CHALLENGE_FORM",
                        aggregationType: "COUNT",
                        secondaryDimensionType: "NONE",
                        fieldTransform: "NONE",
                      });
                    } else {
                      setGraphForm({ ...graphForm, graphType: newType });
                    }
                  }}
                  label="Tipo de Gráfico"
                  sx={{
                    borderRadius: "var(--input-border-radius)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "var(--border-color)",
                    },
                  }}
                >
                  {GRAPH_TYPES.map((type) => {
                    const mapDisabledForTraining =
                      type.value === "MAP" && !supportsMap(graphForm.dataSourceEntity);
                    return (
                      <MenuItem
                        key={type.value}
                        value={type.value}
                        disabled={mapDisabledForTraining}
                        sx={mapDisabledForTraining ? { opacity: 0.5 } : undefined}
                      >
                        <Box>
                          <Typography variant="body2">
                            {type.label}
                            {mapDisabledForTraining && " (no disponible)"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "var(--text-secondary)" }}>
                            {type.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Data Source */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={graphForm.graphType === "MAP"}>
                <InputLabel>Fuente de Datos</InputLabel>
                <Select
                  value={graphForm.dataSourceEntity}
                  onChange={(e) => {
                    const entity = e.target.value as DataSourceEntity;
                    const fields = getCatalogFields(entity);
                    const nextGraphType: GraphType =
                      graphForm.graphType === "MAP" && !supportsMap(entity)
                        ? "BAR"
                        : graphForm.graphType;
                    setGraphForm({
                      ...graphForm,
                      dataSourceEntity: entity,
                      dataSourceField: fields?.[0]?.key || "",
                      graphType: nextGraphType,
                      filters: [],
                      // Reset secondary dimension and transform on data source change
                      secondaryDimensionField: "",
                      secondaryDimensionGranularity: "MONTH",
                      fieldTransform: "NONE",
                    });
                    setSelectedFieldInfo(null);
                    setSelectedDateFieldInfo(null);
                    resetFilterForm();
                  }}
                  label="Fuente de Datos"
                >
                  {catalog.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.label}
                      {c.id === "CHALLENGE_FORM" && " (Dinámico)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Field Selection */}
            <Grid item xs={12} sm={6}>
              {graphForm.dataSourceEntity === "CHALLENGE_FORM" ? (
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setFieldSelectorOpen(true)}
                    startIcon={<FolderOpenIcon />}
                    fullWidth
                    sx={{ height: 56 }}
                  >
                    {selectedFieldInfo?.label ||
                      (graphForm.dataSourceField
                        ? getDisplayLabelForKey(
                            graphForm.dataSourceField,
                            graphForm.dataSourceEntity
                          )
                        : graphForm.graphType === "MAP"
                          ? "Seleccionar Campo de Color..."
                          : "Seleccionar Campo del Formulario...")}
                  </Button>
                  {selectedFieldInfo && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0.5, ml: 1, display: "block" }}
                    >
                      {selectedFieldInfo.label}
                    </Typography>
                  )}
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Campo para Agrupar</InputLabel>
                  <Select
                    value={graphForm.dataSourceField}
                    onChange={(e) =>
                      setGraphForm({ ...graphForm, dataSourceField: e.target.value })
                    }
                    label="Campo para Agrupar"
                  >
                    {getAvailableFields().length === 0 ? (
                      <MenuItem disabled>
                        <em>Sin campos disponibles</em>
                      </MenuItem>
                    ) : (
                      getAvailableFields().map((field) => (
                        <MenuItem key={field.key} value={field.key}>
                          {field.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            </Grid>

            {/* Aggregation */}
            {graphForm.graphType !== "MAP" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Agregación</InputLabel>
                  <Select
                    value={graphForm.aggregationType}
                    onChange={(e) =>
                      setGraphForm({
                        ...graphForm,
                        aggregationType: e.target.value as AggregationType,
                      })
                    }
                    label="Agregación"
                  >
                    {AGGREGATION_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* MAP: Transparency Field Selector */}
            {graphForm.graphType === "MAP" && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  onClick={() => setDateFieldSelectorOpen(true)}
                  startIcon={<FolderOpenIcon />}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {selectedDateFieldInfo?.label ||
                    (graphForm.secondaryDimensionField
                      ? getDisplayLabelForKey(
                          graphForm.secondaryDimensionField,
                          graphForm.dataSourceEntity
                        )
                      : "Seleccionar Campo de Transparencia (Opcional)...")}
                </Button>
                {selectedDateFieldInfo && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 0.5, ml: 1, display: "block" }}
                  >
                    {selectedDateFieldInfo.label}
                  </Typography>
                )}
              </Grid>
            )}

            {/* Field Transform Section - Hidden for MAP */}
            {graphForm.graphType !== "MAP" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Transformación de Campo
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Transformar</InputLabel>
                  <Select
                    value={graphForm.fieldTransform}
                    onChange={(e) =>
                      setGraphForm({
                        ...graphForm,
                        fieldTransform: e.target.value as FieldTransform,
                      })
                    }
                    label="Transformar"
                  >
                    {FIELD_TRANSFORMS.map((transform) => (
                      <MenuItem key={transform.value} value={transform.value}>
                        <Box>
                          <Typography variant="body2">{transform.label}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {transform.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Secondary Dimension Section - Hidden for MAP */}
            {graphForm.graphType !== "MAP" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Dimensión Secundaria</Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo de Dimensión</InputLabel>
                      <Select
                        value={graphForm.secondaryDimensionType}
                        onChange={(e) => {
                          const newType = e.target.value as SecondaryDimensionType;
                          setGraphForm({
                            ...graphForm,
                            secondaryDimensionType: newType,
                            secondaryDimensionField:
                              newType === "NONE" ? "" : graphForm.secondaryDimensionField,
                          });
                          if (newType === "NONE") {
                            setSelectedDateFieldInfo(null);
                          }
                        }}
                        label="Tipo de Dimensión"
                      >
                        {SECONDARY_DIMENSION_TYPES.map((dimType) => (
                          <MenuItem key={dimType.value} value={dimType.value}>
                            <Box>
                              <Typography variant="body2">{dimType.label}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {dimType.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Secondary Dimension Field Selector - uses its own data source */}
                  <DimensionFieldSelector
                    label="Campo de Dimensión"
                    dataSource={graphForm.secondaryDimensionDataSource}
                    field={graphForm.secondaryDimensionField}
                    dimensionType={graphForm.secondaryDimensionType}
                    granularity={graphForm.secondaryDimensionGranularity}
                    showDataSourceSelector={true}
                    primaryDataSource={graphForm.dataSourceEntity}
                    onDataSourceChange={(source) => {
                      setGraphForm({
                        ...graphForm,
                        secondaryDimensionDataSource: source,
                        secondaryDimensionField: "",
                      });
                      setSelectedDateFieldInfo(null);
                    }}
                    onFieldChange={(field) => {
                      // Prefix predefined fields with data source (e.g., "FARM.region")
                      // Form questions (UUIDs) are saved without prefix
                      const isFormQuestion = field.match(
                        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
                      );
                      const prefixedField = isFormQuestion
                        ? field
                        : `${graphForm.secondaryDimensionDataSource}.${field}`;
                      setGraphForm({ ...graphForm, secondaryDimensionField: prefixedField });
                      setSelectedDateFieldInfo(null);
                    }}
                    onGranularityChange={(granularity) => {
                      setGraphForm({ ...graphForm, secondaryDimensionGranularity: granularity });
                    }}
                    onOpenFormSelector={() => setDateFieldSelectorOpen(true)}
                    selectedFieldLabel={selectedDateFieldInfo?.label || undefined}
                    getDisplayLabel={getDisplayLabelForKey}
                    organizationId={selectedOrganization?.id || ""}
                    size="small"
                  />
                </Grid>
              </Grid>
            )}

            {/* Graph Filters */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Filtros del Gráfico</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAdvancedFilterMode}
                      onChange={(e) => setIsAdvancedFilterMode(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Avanzado"
                />
              </Box>

              {/* Filter Cards */}
              {graphForm.filters.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {graphForm.filters.map((filter) => {
                    const field = getFieldByKey(filter.filterField);
                    return (
                      <FilterCard
                        key={filter.filterField}
                        filter={filter}
                        fieldLabel={field?.label || filter.filterField}
                        fieldType={field?.type}
                        enumValues={field?.enumValues}
                        dataSourceEntity={field?.dataSourceEntity}
                        isAdvancedMode={isAdvancedFilterMode}
                        availableFields={getAllFilterableFields()}
                        onUpdateOperator={(op) =>
                          handleUpdateFilterOperator(filter.filterField, op)
                        }
                        onAddValue={(val) => handleAddFilterValue(filter.filterField, val)}
                        onRemoveValue={(val) => handleRemoveFilterValue(filter.filterField, val)}
                        onRemove={() => handleRemoveGraphFilter(filter.filterField)}
                      />
                    );
                  })}
                </Box>
              )}

              {/* Add Filter Control */}
              <AddFilterControl
                availableFields={getAllFilterableFields()}
                isAdvancedMode={isAdvancedFilterMode}
                onAddFilter={handleAddGraphFilter}
                isLoading={false}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 32px",
            borderTop: "1px solid var(--border-color)",
            backgroundColor: "var(--background-paper)",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isLoading}
            sx={{
              fontWeight: 500,
              borderRadius: "var(--button-border-radius)",
              color: "var(--text-primary)",
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!graphForm.name.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
            sx={{
              fontWeight: 600,
              borderRadius: "var(--button-border-radius)",
              backgroundColor: "var(--primary)",
              "&:hover": {
                backgroundColor: "#1a2244",
              },
              "&:disabled": {
                backgroundColor: "var(--text-disabled)",
              },
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHALLENGE_FORM Field Selector Modal */}
      {selectedOrganization?.id && (
        <ChallengeFormFieldSelectorModal
          open={fieldSelectorOpen}
          onClose={() => setFieldSelectorOpen(false)}
          onFieldSelected={handleChallengeFormFieldSelected}
          organizationId={selectedOrganization.id}
          filterByType={graphForm.graphType === "MAP" ? ["enum"] : undefined}
          title={graphForm.graphType === "MAP" ? "Seleccionar Campo de Color" : undefined}
        />
      )}

      {/* Secondary Dimension Field Selector Modal */}
      {selectedOrganization?.id && (
        <ChallengeFormFieldSelectorModal
          open={dateFieldSelectorOpen}
          onClose={() => setDateFieldSelectorOpen(false)}
          onFieldSelected={handleDateFieldSelected}
          organizationId={selectedOrganization.id}
          filterByType={
            graphForm.graphType === "MAP"
              ? ["number", "date"]
              : graphForm.secondaryDimensionType === "TIME"
                ? ["date", "datetime"]
                : undefined
          }
          title={
            graphForm.graphType === "MAP"
              ? "Seleccionar Campo de Transparencia"
              : "Seleccionar Campo para Dimensión Secundaria"
          }
        />
      )}
    </>
  );
};
